import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  LineChart, Line, Cell,
} from "recharts";
import { DownloadSimpleIcon, ChartBarIcon, FilePdfIcon, WalletIcon } from "@phosphor-icons/react";
import { exportNodeToPdf } from "@/lib/pdfExport";

const PRESETS = [
  { l: "This Week", days: 6 },
  { l: "Last 30 days", days: 29 },
  { l: "Last 90 days", days: 89 },
];

// Stable chart config objects — extracted so React sees the same reference each render.
const BAR_CHART_MARGIN = { top: 8, right: 8, left: -12, bottom: 0 };
const BAR_TICK = { fontSize: 13 };
const BAR_TOOLTIP_STYLE = { borderRadius: 0, border: "1px solid #0F172A", fontSize: 14 };
const BAR_LEGEND_STYLE = { fontSize: 14 };
const LINE_CHART_MARGIN = { top: 8, right: 8, left: -12, bottom: 0 };
const LINE_TICK = { fontSize: 11 };
const LINE_TOOLTIP_STYLE = { borderRadius: 0, border: "1px solid #0F172A", fontSize: 12 };
const LINE_LEGEND_STYLE = { fontSize: 12 };

function iso(d) { return d.toISOString().slice(0, 10); }
function subDays(d, n) { const x = new Date(d); x.setDate(x.getDate() - n); return x; }

export default function Reports() {
  const today = new Date();
  const [from, setFrom] = useState(iso(subDays(today, 29)));
  const [to, setTo] = useState(iso(today));
  const [data, setData] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [stockStmt, setStockStmt] = useState(null);
  const [stockGran, setStockGran] = useState("daily");
  const [dailyStock, setDailyStock] = useState(null);
  const [colDaily, setColDaily] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const results = await Promise.allSettled([
      api.get(`/reports?from_date=${from}&to_date=${to}`),
      api.get(`/reports/pnl?from_date=${from}&to_date=${to}`),
      api.get(`/reports/stock-statement?from_date=${from}&to_date=${to}&granularity=${stockGran}`),
      api.get(`/reports/opening-closing-stock?from_date=${from}&to_date=${to}&granularity=daily`),
      api.get(`/reports/collections-daily?from_date=${from}&to_date=${to}`),
    ]);
    const [r, p, s, oc, cd] = results;
    if (r.status === "fulfilled") setData(r.value.data); else setData(null);
    if (p.status === "fulfilled") setPnl(p.value.data); else setPnl(null);
    if (s.status === "fulfilled") setStockStmt(s.value.data); else setStockStmt(null);
    if (oc.status === "fulfilled") setDailyStock(oc.value.data); else setDailyStock(null);
    if (cd.status === "fulfilled") setColDaily(cd.value.data); else setColDaily(null);
    const failed = results.filter((x) => x.status === "rejected");
    if (failed.length) {
      const err = failed[0].reason?.response?.data?.detail || failed[0].reason?.message || "Unknown error";
      setLoadError(String(err));
      toast.error(`Some report data failed to load (${failed.length}/${results.length})`);
    }
    setLoading(false);
  }, [from, to, stockGran]);

  useEffect(() => { load(); }, [load]);

  const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const chartData = (data?.days || []).map((d) => ({
    date: d.date.slice(5),
    Cash: d.collections.cash,
    "Google Pay": d.collections.gpay,
    Wallet: d.collections.wallet,
    Collections: d.collection_total,
    Expenses: d.expense_total,
    Net: d.net,
  }));

  const exportCsv = (entity) => {
    const url = `${API}/export.csv?entity=${entity}&from_date=${from}&to_date=${to}`;
    window.open(url, "_blank");
  };

  const exportPdf = async () => {
    if (!printRef.current) return;
    try {
      await exportNodeToPdf(printRef.current, `report-${from}-to-${to}.pdf`, { orientation: "portrait" });
      toast.success("PDF downloaded");
    } catch { toast.error("PDF export failed"); }
  };

  const applyPreset = (days) => {
    setFrom(iso(subDays(today, days)));
    setTo(iso(today));
  };

  return (
    <div className="space-y-10" data-testid="reports-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-slate-500">Analysis</div>
          <h1 className="font-display text-4xl font-black">Reports</h1>
          <p className="text-slate-500 mt-2">Weekly and monthly view of collections, expenses, cylinder movements.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.l}
                onClick={() => applyPreset(p.days)}
                className="px-3 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:border-slate-900 hover:bg-slate-50"
                data-testid={`preset-${p.days}`}
              >
                {p.l}
              </button>
            ))}
          </div>
          <div>
            <Label className="overline">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="report-from" />
          </div>
          <div>
            <Label className="overline">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="report-to" />
          </div>
          <Button variant="outline" onClick={exportPdf} className="rounded-none border-slate-900 h-9" data-testid="reports-export-pdf">
            <FilePdfIcon size={14} className="mr-2" /> Download PDF
          </Button>
        </div>
      </div>

      {/* Load-error banner — surfaces backend failures instead of a blank page */}
      {loadError && (
        <div className="border border-[#FF2400] bg-red-50 p-4 flex items-start gap-3" data-testid="reports-load-error">
          <div className="flex-1">
            <div className="overline text-[#FF2400]">Report load failed</div>
            <div className="text-sm text-slate-700 mt-1 font-mono-num break-all">{loadError}</div>
            <button onClick={load} className="mt-2 text-xs uppercase tracking-wider text-brand-blue hover:text-brand-orange-ink" data-testid="reports-retry-btn">
              ↻ Retry
            </button>
          </div>
        </div>
      )}
      {loading && !data && !pnl && !stockStmt && (
        <div className="border border-slate-200 bg-white p-8 text-center text-slate-400 overline" data-testid="reports-loading">
          Loading report…
        </div>
      )}

      <div ref={printRef}>{/* Totals row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200 bg-white">
        <Kpi label="Collections" value={money(data?.totals?.collection)} testid="kpi-r-col" />
        <Kpi label="Expenses" value={money(data?.totals?.expense)} testid="kpi-r-exp" />
        <Kpi label="Net" value={money(data?.totals?.net)} accent testid="kpi-r-net" />
        <Kpi label="Days" value={data?.days?.length || 0} testid="kpi-r-days" />
      </div>

      {/* Collections stacked bar */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-slate-500">Collections by mode</div>
          <Button variant="outline" onClick={() => exportCsv("collections")} className="rounded-none border-slate-900 h-9" data-testid="export-collections">
            <DownloadSimpleIcon size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
        <div className="border border-slate-200 bg-white p-6">
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={BAR_CHART_MARGIN}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#94A3B8" tick={BAR_TICK} />
                <YAxis stroke="#94A3B8" tick={BAR_TICK} />
                <Tooltip contentStyle={BAR_TOOLTIP_STYLE} />
                <Legend wrapperStyle={BAR_LEGEND_STYLE} />
                <Bar dataKey="Cash" stackId="a" fill="#0F172A" />
                <Bar dataKey="Google Pay" stackId="a" fill="#002FA7" />
                <Bar dataKey="Wallet" stackId="a" fill="#FBBF24" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Net trend line */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-slate-500">Net cash flow (Collections − Expenses)</div>
          <Button variant="outline" onClick={() => exportCsv("expenses")} className="rounded-none border-slate-900 h-9" data-testid="export-expenses">
            <DownloadSimpleIcon size={14} className="mr-2" /> Export Expenses CSV
          </Button>
        </div>
        <div className="border border-slate-200 bg-white p-6">
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={LINE_CHART_MARGIN}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#94A3B8" tick={LINE_TICK} />
                <YAxis stroke="#94A3B8" tick={LINE_TICK} />
                <Tooltip contentStyle={LINE_TOOLTIP_STYLE} />
                <Legend wrapperStyle={LINE_LEGEND_STYLE} />
                <Line type="monotone" dataKey="Collections" stroke="#002FA7" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Expenses" stroke="#FF2400" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Net" stroke="#0F172A" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* P&L section */}
      {pnl && (
        <section>
          <div className="overline text-slate-500 mb-3">Profit &amp; Loss</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="border border-slate-200 bg-white p-6">
              <div className="overline text-slate-500 mb-4">Income by mode</div>
              <div className="space-y-3">
                {Object.entries((pnl.income && pnl.income.by_mode) || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-sm uppercase tracking-wider">{k}</span>
                    <span className="font-mono-num font-semibold">{money(v)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-slate-300">
                  <span className="text-sm uppercase tracking-wider font-bold">Total Income</span>
                  <span className="font-mono-num font-bold text-lg">{money(pnl.income?.total)}</span>
                </div>
              </div>
            </div>
            <div className="border border-slate-200 bg-white p-6">
              <div className="overline text-slate-500 mb-4">Expense split (mode)</div>
              <div className="space-y-3">
                {Object.entries((pnl.expense && pnl.expense.by_mode) || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-sm uppercase tracking-wider">{k}</span>
                    <span className="font-mono-num text-[#FF2400]">{money(v)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-slate-300">
                  <span className="text-sm uppercase tracking-wider font-bold">Total Expense</span>
                  <span className="font-mono-num font-bold text-lg text-[#FF2400]">{money(pnl.expense?.total)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className={`mt-6 border p-6 flex items-center justify-between ${pnl.net_profit >= 0 ? "border-emerald-600 bg-emerald-50/50" : "border-[#FF2400] bg-[#FF2400]/5"}`} data-testid="net-profit-box">
            <div>
              <div className="overline text-slate-500">Net Profit</div>
              <div className="text-xs text-slate-500 mt-1">Income − Expense · {pnl.from} → {pnl.to}</div>
            </div>
            <div className={`font-mono-num font-bold text-3xl ${pnl.net_profit >= 0 ? "text-emerald-700" : "text-[#FF2400]"}`}>
              {money(pnl.net_profit)}
            </div>
          </div>
          {(pnl.customer_billing?.billed || 0) > 0 && (
            <div className="mt-4 border border-slate-200 p-4 grid grid-cols-3 gap-4 text-sm">
              <div><div className="overline text-slate-500">Customer billed</div><div className="font-mono-num font-semibold mt-1">{money(pnl.customer_billing.billed)}</div></div>
              <div><div className="overline text-slate-500">Customer collected</div><div className="font-mono-num font-semibold mt-1">{money(pnl.customer_billing.collected)}</div></div>
              <div><div className="overline text-slate-500">New Outstanding (range)</div><div className="font-mono-num font-semibold text-[#FF2400] mt-1">{money(pnl.customer_billing.outstanding_delta)}</div></div>
            </div>
          )}
        </section>
      )}

      {/* Expenses breakdown per category */}
      {pnl && <ExpensesByCategory pnl={pnl} money={money} exportCsv={exportCsv} /> }

      {/* Movements totals */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-slate-500">Cylinder Movements (period totals)</div>
          <Button variant="outline" onClick={() => exportCsv("transactions")} className="rounded-none border-slate-900 h-9" data-testid="export-transactions">
            <DownloadSimpleIcon size={14} className="mr-2" /> Export Movements CSV
          </Button>
        </div>
        <div className="border border-slate-200 bg-white">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8">
            {Object.entries(data?.totals?.transactions || {}).map(([k, v]) => (
              <div key={k} className="border-r border-b border-slate-200 last:border-r-0 p-4">
                <div className="overline text-slate-500" style={{ fontSize: 12 }}>{k.replace(/_/g, " ")}</div>
                <div className="font-mono-num text-2xl font-bold mt-2">{v}</div>
              </div>
            ))}
            {Object.keys(data?.totals?.transactions || {}).length === 0 && (
              <div className="col-span-full text-center text-slate-400 py-8 text-sm">No movements in this range.</div>
            )}
          </div>
        </div>
      </section>

      {/* Stock Statement — per-product IN/OUT on the selected granularity */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-brand-orange-ink">Product-wise flow</div>
            <h2 className="font-display text-3xl font-black text-brand-blue">Stock Statement</h2>
            <p className="text-slate-500 text-sm mt-1">Total IN &amp; OUT per cylinder type, driven by every logged transaction (including commercial-customer deliveries).</p>
          </div>
          <div className="flex gap-2">
            {["daily", "monthly"].map((g) => (
              <button key={g} onClick={() => setStockGran(g)}
                className={`px-3 py-2 text-xs uppercase tracking-wider border ${stockGran === g ? "border-brand-blue bg-brand-blue text-white" : "border-slate-300 text-slate-600 hover:border-brand-blue"}`}
                data-testid={`stock-gran-${g}`}>{g}</button>
            ))}
          </div>
        </div>

        {stockStmt && stockStmt.totals_by_type && stockStmt.totals_by_type.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border border-slate-200 bg-white mb-4">
            {stockStmt.totals_by_type.map((t) => (
              <div key={t.cylinder_type_id} className="p-5 border-r border-b border-slate-200 last:border-r-0" data-testid={`stock-total-${t.cylinder_type_id}`}>
                <div className="overline text-brand-orange-ink">{t.cylinder_name}</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-sm font-mono-num">
                  <div><div className="text-xs text-slate-500 uppercase">In</div><div className="font-bold text-emerald-600">{t.in}</div></div>
                  <div><div className="text-xs text-slate-500 uppercase">Out</div><div className="font-bold text-[#FF2400]">{t.out}</div></div>
                  <div><div className="text-xs text-slate-500 uppercase">Net</div><div className={`font-bold ${t.net < 0 ? "text-[#FF2400]" : "text-brand-blue"}`}>{t.net}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-brand-blue-tint border-b border-slate-200">
                <th className="uppercase text-xs tracking-wider text-brand-blue text-left p-3">{stockGran === "daily" ? "Date" : "Month"}</th>
                <th className="uppercase text-xs tracking-wider text-brand-blue text-left p-3">Cylinder</th>
                <th className="uppercase text-xs tracking-wider text-brand-blue text-right p-3">In</th>
                <th className="uppercase text-xs tracking-wider text-brand-blue text-right p-3">Out</th>
                <th className="uppercase text-xs tracking-wider text-brand-blue text-right p-3">Net</th>
                <th className="uppercase text-xs tracking-wider text-brand-blue text-left p-3">Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {(stockStmt?.rows || []).map((r, i) => {
                const rowKey = `${r.period}-${r.cylinder_id || r.cylinder_name}`;
                return (
                <tr key={rowKey} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`stock-row-${i}`}>
                  <td className="p-3 font-mono-num text-xs text-slate-600">{r.period}</td>
                  <td className="p-3 font-semibold text-brand-blue">{r.cylinder_name}</td>
                  <td className="p-3 text-right font-mono-num font-semibold text-emerald-600">{r.in}</td>
                  <td className="p-3 text-right font-mono-num font-semibold text-[#FF2400]">{r.out}</td>
                  <td className={`p-3 text-right font-mono-num font-bold ${r.net < 0 ? "text-[#FF2400]" : "text-brand-blue"}`}>{r.net}</td>
                  <td className="p-3 text-xs text-slate-500 font-mono-num">
                    {Object.entries(r.by_kind || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}
                  </td>
                </tr>
                );
              })}
              {(!stockStmt?.rows || stockStmt.rows.length === 0) && (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8 text-sm">No stock movements in this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Day-wise Opening / Closing per product */}
      <DailyOpeningClosingSection data={dailyStock} money={money} />

      {/* Day-wise Collections */}
      <DailyCollectionsSection data={colDaily} money={money} />
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div className={`p-6 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-slate-900 text-white" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className={`overline ${accent ? "text-white/60" : "text-slate-500"}`}>{label}</span>
        <ChartBarIcon size={16} className={accent ? "text-white/70" : "text-slate-400"} />
      </div>
      <div className="font-mono-num text-2xl sm:text-3xl font-bold mt-3">{value}</div>
    </div>
  );
}

/* ═══════════════════════════ EXPENSES BY CATEGORY ═══════════════════════════ */
const CATEGORY_PALETTE = [
  "#FF7900", "#003C71", "#FFC72C", "#0F172A", "#FF2400", "#16A34A",
  "#7C3AED", "#0284C7", "#9333EA", "#F97316", "#059669", "#D97706",
];
const CATEGORY_MARGIN = { top: 8, right: 30, left: 8, bottom: 0 };
const CATEGORY_TICK = { fontSize: 12 };

function ExpensesByCategory({ pnl, money, exportCsv }) {
  const byCategory = (pnl && pnl.expense && pnl.expense.by_category) || {};
  const totalExpense = (pnl && pnl.expense && pnl.expense.total) || 0;

  // Build [{ name, amount, pct }] sorted descending
  const rows = useMemo(() => {
    const arr = Object.entries(byCategory).map(([name, amount]) => ({
      name,
      amount: Number(amount) || 0,
      pct: totalExpense > 0 ? ((Number(amount) || 0) / totalExpense) * 100 : 0,
    }));
    arr.sort((a, b) => b.amount - a.amount);
    return arr;
  }, [byCategory, totalExpense]);

  return (
    <section data-testid="expenses-by-category-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <WalletIcon size={18} className="text-[#FF2400]" />
          <div className="overline text-brand-orange-ink">Expense Analytics</div>
          <h2 className="font-display text-2xl font-black text-brand-blue ml-2">Expenses by Category</h2>
        </div>
        <Button variant="outline" onClick={() => exportCsv("expenses")} className="rounded-none border-slate-900 h-9" data-testid="expenses-export-csv">
          <DownloadSimpleIcon size={14} className="mr-2" /> Export Expenses CSV
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="border border-slate-200 bg-white p-8 text-center text-slate-400 text-sm" data-testid="expenses-empty">
          No expenses booked in this range. Add expenses under <span className="text-brand-blue font-semibold">Expenses</span> to see category breakdown.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Horizontal bar chart */}
          <div className="border border-slate-200 bg-white p-4" data-testid="expenses-chart">
            <div style={{ width: "100%", height: Math.max(240, rows.length * 40 + 40) }}>
              <ResponsiveContainer>
                <BarChart data={rows} layout="vertical" margin={CATEGORY_MARGIN}>
                  <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" stroke="#94A3B8" tick={CATEGORY_TICK} tickFormatter={(v) => `₹${(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k`} />
                  <YAxis dataKey="name" type="category" stroke="#0F172A" tick={CATEGORY_TICK} width={110} />
                  <Tooltip
                    contentStyle={{ borderRadius: 0, border: "1px solid #0F172A", fontSize: 13 }}
                    formatter={(v) => [money(v), "Amount"]}
                  />
                  <Bar dataKey="amount" radius={[0, 2, 2, 0]}>
                    {rows.map((r, i) => (
                      <Cell key={r.name} fill={CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-category table with amount + % share bar */}
          <div className="border border-slate-200 bg-white overflow-hidden" data-testid="expenses-table">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="overline text-slate-500">{rows.length} categor{rows.length > 1 ? "ies" : "y"}</div>
              <div className="font-mono-num font-bold text-[#FF2400]">{money(totalExpense)}</div>
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {rows.map((r, i) => (
                <div key={r.name} className="px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`expense-cat-row-${r.name}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3" style={{ background: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length] }} />
                      <span className="font-semibold text-brand-blue">{r.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="overline text-slate-500" style={{ fontSize: 11 }}>{r.pct.toFixed(1)}%</span>
                      <span className="font-mono-num text-[#FF2400] font-semibold">{money(r.amount)}</span>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 mt-1">
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.max(2, r.pct)}%`,
                        background: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


/* ═══════════════════════════ DAY-WISE OPENING / CLOSING ═══════════════════════════ */
function DailyOpeningClosingSection({ data, money }) {
  const rows = data?.rows || [];
  if (!rows.length) return null;
  // Group by date for readability
  const groups = rows.reduce((m, r) => { (m[r.date] = m[r.date] || []).push(r); return m; }, {});
  return (
    <section data-testid="daily-open-close-section" className="pt-2">
      <div className="flex items-center gap-2 mb-3">
        <ChartBarIcon size={18} className="text-brand-blue" />
        <h2 className="font-display text-2xl font-black text-brand-blue">Day-wise Opening &amp; Closing Stock</h2>
        <span className="overline text-slate-400 ml-2">{data.from_date} → {data.to_date}</span>
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[70vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <th className="text-left p-2 uppercase text-xs tracking-wider border-b border-slate-200">Date</th>
              <th className="text-left p-2 uppercase text-xs tracking-wider border-b border-slate-200">Cylinder</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Open Full</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Open Empty</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Close Full</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Close Empty</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Δ Full</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Δ Empty</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groups).map(([date, grp]) => grp.map((r, i) => (
              <tr key={`${date}-${r.id}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`daily-oc-${date}-${r.id}`}>
                <td className="p-2 font-mono-num text-slate-600 text-xs">{i === 0 ? date : ""}</td>
                <td className="p-2 font-semibold text-brand-blue">{r.name}</td>
                <td className="p-2 text-right font-mono-num">{r.opening_full}</td>
                <td className="p-2 text-right font-mono-num">{r.opening_empty}</td>
                <td className="p-2 text-right font-mono-num font-semibold">{r.closing_full}</td>
                <td className="p-2 text-right font-mono-num font-semibold">{r.closing_empty}</td>
                <td className={`p-2 text-right font-mono-num font-semibold ${r.delta_full > 0 ? "text-emerald-700" : r.delta_full < 0 ? "text-[#FF2400]" : "text-slate-400"}`}>{r.delta_full > 0 ? "+" : ""}{r.delta_full}</td>
                <td className={`p-2 text-right font-mono-num font-semibold ${r.delta_empty > 0 ? "text-emerald-700" : r.delta_empty < 0 ? "text-[#FF2400]" : "text-slate-400"}`}>{r.delta_empty > 0 ? "+" : ""}{r.delta_empty}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ═══════════════════════════ DAY-WISE COLLECTIONS ═══════════════════════════ */
function DailyCollectionsSection({ data, money }) {
  const days = data?.days || [];
  const totals = data?.totals || { cash: 0, gpay: 0, wallet: 0, total: 0, count: 0 };
  if (!days.length) return null;
  return (
    <section data-testid="daily-collections-section" className="pt-2">
      <div className="flex items-center gap-2 mb-3">
        <WalletIcon size={18} className="text-brand-blue" />
        <h2 className="font-display text-2xl font-black text-brand-blue">Day-wise Collections</h2>
        <span className="overline text-slate-400 ml-2">{data.from_date} → {data.to_date}</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border border-slate-200 bg-white mb-4">
        <Kpi label="Cash" value={money(totals.cash)} testid="dc-cash" />
        <Kpi label="Google Pay" value={money(totals.gpay)} testid="dc-gpay" />
        <Kpi label="Wallet / IOC" value={money(totals.wallet)} testid="dc-wallet" />
        <Kpi label="Count" value={totals.count} testid="dc-count" />
        <Kpi label="Total" value={money(totals.total)} accent testid="dc-total" />
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto max-h-[70vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr>
              <th className="text-left p-2 uppercase text-xs tracking-wider border-b border-slate-200">Date</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Cash</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Google Pay</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Wallet</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Day Total</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Running</th>
              <th className="text-right p-2 uppercase text-xs tracking-wider border-b border-slate-200">Count</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.date} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`daily-col-${d.date}`}>
                <td className="p-2 font-mono-num text-slate-700">{d.date}</td>
                <td className="p-2 text-right font-mono-num">{money(d.cash)}</td>
                <td className="p-2 text-right font-mono-num">{money(d.gpay)}</td>
                <td className="p-2 text-right font-mono-num">{money(d.wallet)}</td>
                <td className="p-2 text-right font-mono-num font-semibold">{money(d.total)}</td>
                <td className="p-2 text-right font-mono-num text-brand-blue font-semibold">{money(d.running_total)}</td>
                <td className="p-2 text-right font-mono-num text-slate-500">{d.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
